import { useEffect, useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import { adminGet, adminPost, adminPut, adminDel, authHeaders } from "@/lib/adminApi";
import { capFirst } from "@/lib/profile";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Champs texte admin : 1re lettre en majuscule automatiquement
const CAP_KEYS = new Set(["name", "title", "presentation", "skills", "text", "availability", "category", "description"]);

export default function Directory({ title, endpoint, fields, columns, testIdPrefix }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(null);

  const load = () => adminGet(`/${endpoint}`).then((d) => setRows(d[endpoint] || [])).catch(() => setRows([]));
  useEffect(() => {
    load();
  }, [endpoint]);

  const save = async () => {
    const payload = { ...form };
    for (const f of fields) {
      if (f.type === "number") payload[f.key] = Number(payload[f.key]) || 0;
      if (f.type === "array") payload[f.key] = String(payload[f.key] || "").split(",").map((s) => s.trim()).filter(Boolean);
      if (f.type === "bool") payload[f.key] = payload[f.key] === true || payload[f.key] === "oui";
    }
    try {
      if (form.id) await adminPut(`/${endpoint}/${form.id}`, payload);
      else await adminPost(`/${endpoint}`, payload);
      toast("Enregistré.");
      setForm(null);
      load();
    } catch {
      toast("Une erreur est survenue.");
    }
  };

  const remove = async (r) => {
    await adminDel(`/${endpoint}/${r.id}`).catch(() => {});
    toast("Supprimé.");
    load();
  };

  return (
    <div data-testid={`admin-${testIdPrefix}-module`}>
      <h2 className="rav-admin-h2">{title}</h2>
      <button type="button" className="rav-primary-button rav-admin-create" data-testid={`${testIdPrefix}-create`} onClick={() => setForm({})}>
        + Ajouter
      </button>

      {form && (
        <div className="rav-admin-form" data-testid={`${testIdPrefix}-form`}>
          {fields.map((f) =>
            f.type === "photo" ? (
              <div key={f.key} className="rav-field rav-admin-photo">
                <span className="rav-admin-photo-label">{f.label}</span>
                {form[f.key] && (
                  <img className="rav-admin-photo-preview" data-testid={`${testIdPrefix}-form-${f.key}-preview`} src={`${API}/media/${form[f.key]}`} alt="Aperçu" />
                )}
                <input
                  type="file"
                  accept="image/*"
                  className="rav-input"
                  data-testid={`${testIdPrefix}-form-${f.key}`}
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    try {
                      const data = new FormData();
                      data.append("file", file);
                      const { data: up } = await axios.post(`${API}/admin/media`, data, { headers: authHeaders() });
                      setForm((prev) => ({ ...prev, [f.key]: up.storage_path }));
                      toast("Photo enregistrée.");
                    } catch {
                      toast("Le téléversement de la photo a échoué.");
                    } finally {
                      e.target.value = "";
                    }
                  }}
                />
              </div>
            ) : f.type === "bool" ? (
              <label key={f.key} className="rav-field rav-admin-check">
                <input type="checkbox" data-testid={`${testIdPrefix}-form-${f.key}`} checked={form[f.key] === true || form[f.key] === "oui"} onChange={(e) => setForm({ ...form, [f.key]: e.target.checked ? "oui" : "non" })} />
                <span>{f.label}</span>
              </label>
            ) : (
              <input
                key={f.key}
                className="rav-input"
                data-testid={`${testIdPrefix}-form-${f.key}`}
                type={f.type === "number" ? "number" : "text"}
                step="0.01"
                placeholder={f.label}
                value={Array.isArray(form[f.key]) ? form[f.key].join(", ") : form[f.key] ?? ""}
                onChange={(e) => setForm({ ...form, [f.key]: CAP_KEYS.has(f.key) ? capFirst(e.target.value) : e.target.value })}
              />
            ),
          )}
          <div className="rav-session-actions">
            <button type="button" className="rav-secondary-button rav-session-btn" data-testid={`${testIdPrefix}-form-save`} onClick={save}>
              Enregistrer
            </button>
            <button type="button" className="rav-text-link" data-testid={`${testIdPrefix}-form-cancel`} onClick={() => setForm(null)}>
              Annuler
            </button>
          </div>
        </div>
      )}

      <div className="rav-admin-list">
        {rows.map((r, i) => (
          <div key={r.id} className="rav-admin-row" data-testid={`${testIdPrefix}-row-${i}`}>
            <span className="rav-admin-row-label">{columns[0] ? r[columns[0]] : r.name}</span>
            <span className="rav-admin-row-sub">
              {columns.slice(1).map((c) => (Array.isArray(r[c]) ? r[c].join(", ") : r[c])).filter(Boolean).join(" · ")}
            </span>
            <span className="rav-admin-row-actions">
              <button type="button" className="rav-text-link" data-testid={`${testIdPrefix}-edit-${i}`} onClick={() => setForm({ ...r })}>
                Modifier
              </button>
              <button type="button" className="rav-text-link" data-testid={`${testIdPrefix}-delete-${i}`} onClick={() => remove(r)}>
                Supprimer
              </button>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
